import { AnimatePresence, motion } from 'framer-motion';
import { TextAreaField } from '~/components/shared/forms';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Divider,
  FieldValue,
  InputOfficeDropdown,
  InputSelect,
  Label,
  MultiSelectGroup,
  NumberField,
  RadioOption,
} from '~/commonComponents';
import type { ISelectOption } from '~/commonComponents/InputSelect';
import { EXCLUDED_PROFESSIONS } from '~/components/PermanentJobsV2/Constants';
import {
  benefitsOptions,
  benefitsOtherMaxLength,
  jobTitleMaxLength,
} from '~/components/PermanentJobsV2/utils';
import { CompensationType, PayType } from '~/domain/PermanentJobs/enums';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import {
  useIsParentOfficeSelector,
  useProfessionOptions,
  useSpecialtyOptions,
  useUserSelector,
  useUserSpecialty,
} from '~/hooks';
import { ProfessionTypeEnum } from '~/models';
import { EnumHelper } from '~/utils/EnumHelper';
import { IBaseFormProps } from '../../Types';
import { getRateSuffix } from '../../utils/formatters';

export const GeneralJobInfoForm = ({
  jobPosting,
  control,
  errors,
  setValue,
  readonlyFields,
}: IBaseFormProps) => {
  const user = useUserSelector();
  const isParentOffice = useIsParentOfficeSelector();
  const userSpecialty = useUserSpecialty();

  // Watch specific fields for reactive logic
  const professionType = useWatch({ control, name: 'professionType' });
  const officeSpecialty = useWatch({ control, name: 'officeSpecialty' });
  const compensationType = useWatch({ control, name: 'compensationType' });
  const benefits = useWatch({ control, name: 'benefits' });
  const payType = useWatch({ control, name: 'payType' });

  const { options: professionOptions } = useProfessionOptions({ excludes: EXCLUDED_PROFESSIONS });
  const optProfessionTypes = professionOptions.map(
    ({ label, value }) => ({ title: label, value }) as ISelectOption,
  );
  const { isLoading: isGetUserEducationLoading, options: optSpecialty } = useSpecialtyOptions(
    professionType || ProfessionTypeEnum.RDH,
  );

  // Get profession display name
  const professionDisplay = useMemo(() => {
    if (!professionType) return '';

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(professionType);
    return isSubProfession
      ? UserSubProfession.getName(professionType)
      : UserProfession.getName(professionType);
  }, [professionType]);

  const payRateLabel = useMemo(() => getRateSuffix(payType), [payType]);

  // Validate and reset specialty when profession type changes
  useEffect(() => {
    if (optSpecialty.length === 0) return;

    // Check if current specialty is valid in the new options
    const isCurrentSpecialtyValid = optSpecialty.some((option) => option.value === officeSpecialty);

    if (!isCurrentSpecialtyValid) {
      // Reset to user's specialty if available, otherwise default to first option
      const defaultSpecialty = optSpecialty.some((option) => option.value === userSpecialty)
        ? userSpecialty
        : optSpecialty[0]?.value;

      if (defaultSpecialty) {
        setValue?.('officeSpecialty', defaultSpecialty);
      }
    }
    // If valid, keep the current specialty (do nothing)
  }, [professionType, officeSpecialty, setValue, userSpecialty, optSpecialty]);

  const handleBenefitsChange = useCallback(
    (selectedValues: string[], fieldOnChange: (...event: any[]) => void) => {
      // Clear benefitsOther if "Other" is deselected
      if (!selectedValues.includes('Other')) {
        setValue?.('benefitsOther', '');
      } else {
        setValue?.('benefitsOther', jobPosting?.benefitsOther || '');
      }

      fieldOnChange(selectedValues);
    },
    [jobPosting?.benefitsOther, setValue],
  );

  return (
    <Column gap={6}>
      <Column>
        <Label text="Office Name" required={isParentOffice} />
        {isParentOffice && !readonlyFields?.includes('office') ? (
          <Controller
            control={control}
            name="office"
            render={({ field }) => (
              <InputOfficeDropdown
                placeholder="Enter or Select Office Name"
                value={field.value || ''}
                onChange={(item) => field.onChange(item?.value)}
                error={!!errors?.office}
              />
            )}
          />
        ) : (
          <Typography as="span" style={{ paddingLeft: 17 }}>{user?.office_name || ''}</Typography>
        )}
      </Column>

      <Divider height={1} marginVertical={0} />

      <Column>
        <Controller
          control={control}
          name="jobTitle"
          render={({ field }) => (
            <FieldValue
              label="Job Title"
              placeholder="Enter Job Title"
              required
              shouldCapitalize={false}
              shouldTrim={true}
              disableEmoji={true}
              value={field.value}
              setValue={field.onChange}
              customInputStyle={{ width: '100%', boxSizing: 'border-box' }}
              validation={{ maxLength: jobTitleMaxLength }}
              error={!!errors?.jobTitle}
              errorStyle="border"
              dataCy="perm-job-title-input"
            />
          )}
        />
        <Typography italic color={colors.neutral[500]} style={{ marginTop: 8 }}>
          {jobTitleMaxLength} Character Max
        </Typography>
      </Column>

      <Divider height={1} marginVertical={0} />

      <Column>
        <Label text="Professional Type" required />
        {!readonlyFields?.includes('professionType') ? (
          <Controller
            control={control}
            name="professionType"
            render={({ field }) => (
              <InputSelect
                placeholder="Select Professional Type"
                value={field.value}
                setValue={field.onChange}
                isSelectVisible={false}
                options={optProfessionTypes}
                error={!!errors?.professionType}
              />
            )}
          />
        ) : (
          <Typography as="span" style={{ paddingLeft: 17 }}>{professionDisplay || ''}</Typography>
        )}
      </Column>

      <Column>
        <Label text="Specialty" required />
        <Controller
          control={control}
          name="officeSpecialty"
          render={({ field }) => (
            <InputSelect
              placeholder="Select Specialty"
              value={field.value}
              setValue={field.onChange}
              isSelectVisible={false}
              options={optSpecialty}
              customTitle={field.value ? field.value.split('_')?.[1] || 'General' : undefined}
              error={!!errors?.officeSpecialty}
              isLoading={isGetUserEducationLoading}
            />
          )}
        />
      </Column>

      <Divider marginVertical={0} />

      <Column gap={3}>
        <Label text="Pay Type" required={false} />
        <Controller
          control={control}
          name="payType"
          render={({ field }) => (
            <Row gap={6}>
              <RadioOption
                text="Salary"
                checked={field.value === PayType.SALARY}
                onClick={() => field.onChange(PayType.SALARY)}
              />
              <RadioOption
                text="Hourly"
                checked={field.value === PayType.HOURLY}
                onClick={() => field.onChange(PayType.HOURLY)}
              />
              <RadioOption
                text="Daily"
                checked={field.value === PayType.DAILY}
                onClick={() => field.onChange(PayType.DAILY)}
              />
            </Row>
          )}
        />
      </Column>

      <Column gap={3}>
        <Label text="Compensation" required={false} />
        <Controller
          control={control}
          name="compensationType"
          render={({ field }) => (
            <Column gap={4}>
              <RadioOption
                text="Fixed Amount"
                checked={field.value === CompensationType.FIXED}
                onClick={() => {
                  field.onChange(CompensationType.FIXED);
                  // Clear maxRate when changing to FIXED compensation
                  setValue?.('maxRate', '');
                }}
              />
              <RadioOption
                text="Pay Range (Min - Max)"
                checked={field.value === CompensationType.RANGE}
                onClick={() => field.onChange(CompensationType.RANGE)}
              />
            </Column>
          )}
        />
      </Column>

      {compensationType && (
        <Column>
          <Label
            text={`Stated Pay ${compensationType === CompensationType.RANGE ? 'Range' : ''}`}
            required
          />
          <Row align="center" gap={2}>
            <Typography as="span">$</Typography>
            <Controller
              control={control}
              name="minRate"
              render={({ field }) => (
                <NumberField
                  placeholder="Amount"
                  value={field.value}
                  setValue={field.onChange}
                  currency
                  customInputStyle={{
                    width: compensationType === CompensationType.FIXED ? '200px' : '150px',
                  }}
                  error={!!errors?.minRate}
                />
              )}
            />
            {compensationType === CompensationType.RANGE && (
              <>
                <Typography as="span">-</Typography>
                <Typography as="span">$</Typography>
                <Controller
                  control={control}
                  name="maxRate"
                  render={({ field }) => (
                    <NumberField
                      placeholder="Amount"
                      value={field.value}
                      setValue={field.onChange}
                      currency
                      customInputStyle={{ width: '150px' }}
                      error={!!errors?.maxRate}
                    />
                  )}
                />
              </>
            )}
            <Typography as="span" variant="body1" bold>
              {payRateLabel}
            </Typography>
          </Row>
        </Column>
      )}

      <Column gap={3}>
        <Label text="Benefits Information" required={false} />
        <Controller
          control={control}
          name="benefits"
          render={({ field }) => (
            <MultiSelectGroup
              value={field.value}
              options={benefitsOptions.map((option) => ({
                value: option.code,
                label: option.name,
              }))}
              onChange={(selected) =>
                handleBenefitsChange(
                  selected.map((s) => s.value),
                  field.onChange,
                )
              }
            />
          )}
        />
        <AnimatePresence mode="wait">
          {benefits?.includes('Other') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Column gap={3} className="mt-2">
                <Typography as="span">Enter any other benefits below:</Typography>
                <Controller
                  control={control}
                  name="benefitsOther"
                  render={({ field }) => (
                    <TextAreaField
                      {...field}
                      height={60}
                      maxLength={benefitsOtherMaxLength}
                      placeholder="Enter other benefits here"
                    />
                  )}
                />
              </Column>
            </motion.div>
          )}
        </AnimatePresence>
      </Column>
    </Column>
  );
};
